import { Link, useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { Handshake } from 'lucide-react'
import { qk } from '@/api/query-keys'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import { pipelineApi, usePipelineSettings } from './api'
import { daysAgo, Dot, LEVEL_SOFT, money, NotInGhl, shortDay, STAGE_LABEL, STATUS_LABEL, STATUS_STYLE } from './shared'

/**
 * C4 / P3 — on a Client Project's dashboard: the deal behind it and its pilot card, so the project
 * team sees what sales agreed (success criteria, dates, sponsor) without opening the CRM.
 */
export function PipelinePanel({ projectId }: { projectId: string }) {
  const { workspaceSlug = '' } = useParams()
  const settings = usePipelineSettings()
  const panel = useQuery({ queryKey: qk.pipelineProject(projectId), queryFn: () => pipelineApi.project(projectId), enabled: Boolean(settings.data?.on), staleTime: 60_000, retry: false })
  const deals = panel.data?.deals ?? []
  if (!deals.length) return null
  return (
    <section className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <h2 className="mb-3 flex items-center gap-1.5 text-sm font-semibold"><Handshake className="size-4" />The deal and its pilot <span className="rounded-full bg-info-soft px-2 py-0.5 text-tiny font-semibold text-info">Staging preview</span></h2>
      <div className="grid gap-3">
        {deals.map((d) => (
          <article key={d.id} className="rounded-lg border border-border p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Dot level={d.level} />
              <Link to={`${paths.pipeline(workspaceSlug)}?deal=${encodeURIComponent(d.id)}`} className="font-medium hover:text-brand">{d.name}</Link>
              <span className="rounded-full bg-accent px-2 py-0.5 text-xs">{STAGE_LABEL[d.stage]}</span>
              {!d.inGhl ? <NotInGhl /> : null}
              {d.dealStatus ? <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', STATUS_STYLE[d.dealStatus])}>{STATUS_LABEL[d.dealStatus]}</span> : null}
              <span className="ml-auto text-xs text-muted-foreground">{d.owner.name ?? 'No owner'}{d.value !== null ? ` · ${money(d.value)}` : ''} · contact {daysAgo(d.lastContactAt)}</span>
            </div>
            {d.reasons.length ? <p className={cn('mt-2 rounded-md px-2 py-1 text-xs', LEVEL_SOFT[d.level])}>{d.reasons.join(' · ')}</p> : null}
            <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-4">
              <div className="sm:col-span-4">
                <dt className="text-xs text-muted-foreground">What success looks like</dt>
                <dd>{d.facts.successCriteria ?? <span className="text-warning">Not agreed yet</span>}</dd>
              </div>
              <Fact label="Pilot" value={d.facts.startAt || d.facts.endAt ? `${shortDay(d.facts.startAt)} – ${shortDay(d.facts.endAt)}` : null} />
              <Fact label="Decision" value={d.facts.decisionAt ? shortDay(d.facts.decisionAt) : null} danger={d.decisionOverdue} />
              <Fact label="Sponsor" value={d.facts.sponsorName} />
              <Fact label="Outcome" value={d.facts.outcome === 'go' ? 'Go' : d.facts.outcome === 'no_go' ? 'No go' : 'Not yet'} />
              <div className="sm:col-span-4">
                <dt className="text-xs text-muted-foreground">Next step</dt>
                <dd>{d.nextStep ? `${d.nextStep}${d.nextStepAt ? ` · by ${shortDay(d.nextStepAt)}` : ''}` : <span className="text-warning">None</span>}</dd>
              </div>
            </dl>
            {d.history.filter((h) => h.kind === 'checkin' || h.kind === 'move').length ? (
              <ul className="mt-3 grid gap-1 border-t border-border-soft pt-2 text-xs text-muted-foreground">
                {d.history.filter((h) => h.kind === 'checkin' || h.kind === 'move').slice(0, 4).map((h, i) => <li key={i}>{shortDay(h.at)} — {h.text}</li>)}
              </ul>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  )
}

function Fact({ label, value, danger }: { label: string; value: string | null; danger?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={cn(danger && 'font-medium text-danger')}>{value ?? <span className="text-warning">Missing</span>}</dd>
    </div>
  )
}
